import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { T, sans, card, topBtn } from '../Vendors/VendorDetails';

interface Questionnaire {
  id: string;
  name: string;
  description: string | null;
  source_file: string | null;
  item_count: number;
  is_active: boolean;
  // Built-in template not included in this workspace's plan: listed by name, but can't be opened, activated or sent.
  locked?: boolean;
  created_at: string;
  updated_at: string;
}

const LIST_KEY = ['questionnaire-library', 'list'];
const EMPTY: Questionnaire[] = [];

type StatusFilter = 'all' | 'active' | 'inactive' | 'custom';
type SortMode = 'recommended' | 'name' | 'questions' | 'updated';

// Shown first (in this order) among inactive questionnaires; everything not listed follows alphabetically.
const PRIORITY_ORDER = [
  'ISO 27001-2022',
  'GDPR Security Standard Questionnaire',
  'HIPAA Security Questionnaire',
  'NIS 2 Supplier Due Diligence',
  'NIST CSF 2.0',
  'Digital Personal Data Protection Act, 2023 (DPDP)',
  'NIST AI Risk Management Framework (AI RMF) 1.0',
  'FluxWatch AI-Risk Essentials Questionnaire',
  'PCI Data Security Standard Questionnaire',
  'PCI DSS v4 - SAQ A',
  'PCI DSS v4 - SAQ A-EP',
  'PCI DSS v4 - SAQ B',
  'PCI DSS v4 - SAQ B-IP',
  'PCI DSS v4 - SAQ C',
  'PCI DSS v4 - SAQ C-VT',
  'PCI DSS v4 - SAQ D for Merchants',
  'PCI DSS v4 - SAQ D for Service Providers',
  'PCI DSS v4 - SAQ P2PE',
  'PCI DSS v4 - SAQ SPoC',
  'NIST SP 800-53 Rev.4 Security Standard Questionnaire',
];
const priorityRank = (name: string) => {
  const i = PRIORITY_ORDER.indexOf(name);
  return i === -1 ? PRIORITY_ORDER.length : i;
};

// A display-only grouping worked out from the template's name -- nothing is stored.
const CATEGORIES = [
  { key: 'ai', label: 'AI governance', color: '#7C3AED', test: /\bAI\b|artificial intelligence/i },
  { key: 'payments', label: 'Payments', color: '#D97706', test: /\bPCI\b|payment/i },
  { key: 'privacy', label: 'Privacy & data protection', color: '#DB2777', test: /GDPR|HIPAA|CCPA|DPDP|privacy|personal data|data protection/i },
  { key: 'security', label: 'Security frameworks', color: '#2563EB', test: /ISO|NIST|SOC ?2|CIS\b|NIS ?2|security|cyber/i },
] as const;
type CategoryKey = typeof CATEGORIES[number]['key'] | 'other';
const OTHER = { key: 'other' as const, label: 'Other', color: '#64748B' };
const categoryOf = (q: Questionnaire) => CATEGORIES.find(c => c.test.test(q.name)) || OTHER;

// Short code shown on the card tile, e.g. "ISO", "PCI", "GDPR".
const monogram = (name: string) => {
  const known = /\b(ISO|NIST|PCI|GDPR|HIPAA|CCPA|DPDP|SOC|NIS|CIS|AI)\b/.exec(name);
  if (known) return known[1];
  return name.split(/\s+/).filter(w => /^[A-Za-z]/.test(w)).slice(0, 2).map(w => w[0].toUpperCase()).join('') || 'Q';
};

const apiError = (error: any, fallback: string) => {
  const d = error?.response?.data?.detail;
  return typeof d === 'string' ? d : (d && typeof d === 'object' && typeof d.message === 'string') ? d.message : fallback;
};
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const Icon = ({ d, size = 14 }: { d: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
);
const I = {
  plus: 'M12 5v14M5 12h14',
  search: 'M11 18a7 7 0 1 1 0-14 7 7 0 0 1 0 14ZM21 21l-4.3-4.3',
  external: 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3',
  trash: 'M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6',
  close: 'M6 6l12 12M18 6 6 18',
  check: 'M5 12l5 5L20 7',
  edit: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4Z',
  grid: 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  doc: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5ZM14 3v5h5M9 13h6M9 17h4',
  lock: 'M6 11h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1ZM8 11V7a4 4 0 0 1 8 0v4',
};

export default function QuestionnaireLibrary() {
  const navigate = useNavigate();
  const { mode } = useCustomTheme();
  const isDark = mode === 'dark';
  const { user } = useAuth();
  const canEdit = !!user && user.role !== 'viewer';
  const canDelete = !!user && (user.role === 'admin' || user.role === 'super_admin');

  const queryClient = useQueryClient();
  const libraryQuery = useQuery({
    queryKey: LIST_KEY,
    queryFn: async () => ((await api.get('/v1/questionnaire-library/')).data.questionnaires || []) as Questionnaire[],
  });
  const questionnaires = libraryQuery.data ?? EMPTY;
  const loading = libraryQuery.isPending || (!libraryQuery.data && libraryQuery.isFetching);
  const loadError = !libraryQuery.data && libraryQuery.isError
    ? apiError(libraryQuery.error, 'Could not load the questionnaire library. Try again.')
    : '';
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [category, setCategory] = useState<CategoryKey | 'all'>('all');
  const [sort, setSort] = useState<SortMode>('recommended');
  const [view, setView] = useState<'grid' | 'list'>(() => {
    try { return (localStorage.getItem('qlib-view') as 'grid' | 'list') || 'grid'; } catch { return 'grid'; }
  });
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Questionnaire | null>(null);
  const [toast, setToast] = useState<{ text: string; error?: boolean } | null>(null);

  const c = {
    ink: isDark ? '#e5e7eb' : T.ink, slate: isDark ? '#94a3b8' : T.slate, muted: isDark ? '#64748b' : T.muted,
    line: isDark ? '#1f1f1f' : T.bdr, surface: isDark ? '#0d0d0d' : T.surface, surface2: isDark ? '#141414' : T.surface2,
    accent: isDark ? '#3B82F6' : T.blue, bg: isDark ? '#000' : T.bg,
  };

  const patchList = (fn: (list: Questionnaire[]) => Questionnaire[]) =>
    queryClient.setQueryData<Questionnaire[]>(LIST_KEY, (prev: Questionnaire[] | undefined) => prev ? fn(prev) : prev);

  useEffect(() => { try { localStorage.setItem('qlib-view', view); } catch { /* storage unavailable */ } }, [view]);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const counts = useMemo(() => ({
    all: questionnaires.length,
    active: questionnaires.filter(q => q.is_active).length,
    inactive: questionnaires.filter(q => !q.is_active).length,
    custom: questionnaires.filter(q => q.source_file === null).length,
  }), [questionnaires]);

  const categoryCounts = useMemo(() => {
    const map: Record<string, number> = {};
    questionnaires.forEach(q => { const k = categoryOf(q).key; map[k] = (map[k] || 0) + 1; });
    return map;
  }, [questionnaires]);

  const activeQuestions = questionnaires.filter(q => q.is_active).reduce((sum, q) => sum + (q.item_count || 0), 0);
  const lockedCount = questionnaires.filter(q => q.locked).length;

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const list = questionnaires.filter(q => {
      if (status === 'active' && !q.is_active) return false;
      if (status === 'inactive' && q.is_active) return false;
      if (status === 'custom' && q.source_file !== null) return false;
      if (category !== 'all' && categoryOf(q).key !== category) return false;
      if (!needle) return true;
      return q.name.toLowerCase().includes(needle) || (q.description || '').toLowerCase().includes(needle);
    });
    return list.sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'questions') return (b.item_count || 0) - (a.item_count || 0) || a.name.localeCompare(b.name);
      if (sort === 'updated') return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      // Recommended: active first (most recently activated on top), then the curated order, then A-Z; locked last.
      if (!!a.locked !== !!b.locked) return a.locked ? 1 : -1;
      if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
      if (a.is_active) return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      const rank = priorityRank(a.name) - priorityRank(b.name);
      return rank !== 0 ? rank : a.name.localeCompare(b.name);
    });
  }, [questionnaires, search, status, category, sort]);

  const toggleMutation = useMutation({
    mutationFn: (q: Questionnaire) => api.patch(`/v1/questionnaire-library/${q.id}`, { is_active: !q.is_active }),
    onMutate: async q => {
      setTogglingId(q.id);
      // Stop an in-flight refetch from overwriting the optimistic switch position.
      await queryClient.cancelQueries({ queryKey: LIST_KEY });
      const now = new Date().toISOString();
      patchList(list => list.map(x => x.id === q.id ? { ...x, is_active: !x.is_active, updated_at: now } : x));
    },
    onSuccess: (_res, q) => setToast({ text: q.is_active ? `"${q.name}" is no longer available to send.` : `"${q.name}" can now be sent to vendors.` }),
    onError: (error, q) => {
      patchList(list => list.map(x => x.id === q.id ? { ...x, is_active: q.is_active, updated_at: q.updated_at } : x));
      setToast({ text: apiError(error, 'Could not update the questionnaire. Try again.'), error: true });
    },
    onSettled: () => setTogglingId(null),
    meta: { invalidates: [['questionnaire-library']] },
  });
  const toggleActive = (q: Questionnaire) => toggleMutation.mutate(q);

  const openPreview = (q: Questionnaire) => window.open(`/questionnaire-library/${q.id}/preview`, '_blank', 'noopener,noreferrer');
  const filtersActive = !!search || status !== 'all' || category !== 'all';
  const clearFilters = () => { setSearch(''); setStatus('all'); setCategory('all'); };

  /* ── styles ──────────────────────────────────────────────────── */
  const segment = (on: boolean): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', fontSize: 13, fontWeight: 600, fontFamily: sans,
    cursor: 'pointer', border: 'none', borderRadius: 6, whiteSpace: 'nowrap', background: on ? c.surface : 'transparent', color: on ? c.ink : c.slate,
    boxShadow: on ? (isDark ? '0 0 0 1px #262626' : '0 1px 2px rgba(15,23,42,.08), 0 0 0 1px #E2E8F0') : 'none',
  });
  const badge: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 99, fontVariantNumeric: 'tabular-nums',
    background: isDark ? '#1a1a1a' : '#EEF2F6', color: c.slate,
  };
  const chip = (on: boolean, color: string): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 12px', borderRadius: 99, fontSize: 13, fontWeight: 600,
    fontFamily: sans, cursor: 'pointer', whiteSpace: 'nowrap',
    border: `1px solid ${on ? color : c.line}`, background: on ? `${color}${isDark ? '24' : '12'}` : c.surface, color: on ? color : c.slate,
  });
  const selectStyle: React.CSSProperties = {
    padding: '8px 30px 8px 12px', fontSize: 13, fontFamily: sans, color: c.ink, background: c.surface, border: `1px solid ${c.line}`,
    borderRadius: 8, outline: 'none', cursor: 'pointer', appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2394A3B8' stroke-width='1.5' fill='none'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 11px center',
  };
  const iconBtn = (danger = false): React.CSSProperties => ({
    width: 34, height: 34, borderRadius: 8, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    border: `1px solid ${c.line}`, background: c.surface, color: danger ? '#DC2626' : c.slate, cursor: 'pointer',
  });

  const Toggle = ({ q }: { q: Questionnaire }) => q.locked ? (
    <span title="Included in paid plans" style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 600, padding: '4px 10px', borderRadius: 99,
      color: '#B45309', background: isDark ? '#451a03' : '#FEF3C7',
    }}>
      <Icon d={I.lock} size={13} /> Paid plans
    </span>
  ) : (
    <button
      onClick={e => { e.stopPropagation(); if (canEdit) toggleActive(q); }}
      disabled={!canEdit || togglingId === q.id}
      role="switch" aria-checked={q.is_active}
      title={canEdit ? (q.is_active ? 'Stop offering this template' : 'Make this template available to send') : 'Only editors can change this'}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: 'none', border: 'none', padding: 0, cursor: canEdit ? 'pointer' : 'default', fontFamily: sans, opacity: togglingId === q.id ? 0.6 : 1 }}
    >
      <span style={{ width: 38, height: 22, borderRadius: 99, position: 'relative', flexShrink: 0, background: q.is_active ? '#16A34A' : (isDark ? '#333' : '#CBD5E1'), transition: 'background .15s' }}>
        <span style={{ position: 'absolute', top: 2, left: q.is_active ? 18 : 2, width: 18, height: 18, borderRadius: 99, background: '#fff', transition: 'left .15s', boxShadow: '0 1px 2px rgba(0,0,0,.2)' }} />
      </span>
      <span style={{ fontSize: 13, fontWeight: 600, color: q.is_active ? '#16A34A' : c.slate }}>{q.is_active ? 'Active' : 'Inactive'}</span>
    </button>
  );

  const Tile = ({ q, size = 44 }: { q: Questionnaire; size?: number }) => {
    const cat = categoryOf(q);
    const code = monogram(q.name);
    return (
      <span style={{
        width: size, height: size, borderRadius: 11, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        background: `${cat.color}${isDark ? '26' : '14'}`, color: cat.color, fontWeight: 800, letterSpacing: '.02em',
        fontSize: code.length > 3 ? size * 0.24 : size * 0.3,
      }}>{code}</span>
    );
  };

  const kpis = [
    { label: 'Templates', value: counts.all, note: `${counts.all - counts.custom} standard · ${counts.custom} custom` },
    { label: 'Active for sending', value: counts.active, note: counts.active ? 'Show up in "Send questionnaire"' : 'Switch one on to start sending', tone: counts.active ? '#16A34A' : undefined },
    { label: 'Questions in active templates', value: activeQuestions.toLocaleString(), note: counts.active ? `Across ${counts.active} template${counts.active === 1 ? '' : 's'}` : '—' },
    { label: 'Frameworks covered', value: Object.keys(categoryCounts).filter(k => k !== 'other').length, note: CATEGORIES.filter(cat => categoryCounts[cat.key]).map(cat => cat.label.split(' ')[0]).join(', ') || '—' },
  ];

  return (
    <div style={{ fontFamily: sans, background: c.bg, minHeight: '100vh', padding: 24 }}>
      <style>{'@keyframes qlPop { from { transform: translateY(6px) scale(.98); opacity: 0 } to { transform: none; opacity: 1 } } @keyframes qlShimmer { 0%,100% { opacity: .55 } 50% { opacity: 1 } } @keyframes qlToast { from { transform: translateY(8px); opacity: 0 } to { transform: none; opacity: 1 } }'}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        <div style={{ maxWidth: 720 }}>
          <div style={{ fontSize: 24, fontWeight: 700, color: c.ink, letterSpacing: '-.01em' }}>Questionnaire Library</div>
          <div style={{ fontSize: 14, color: c.slate, marginTop: 4, lineHeight: 1.55 }}>
            Every questionnaire template your organisation can send to vendors. Switch a template to Active to make it
            available in "Send questionnaire"; switching it off retires it without deleting anything.
          </div>
          {lockedCount > 0 && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 10, fontSize: 13, color: '#B45309' }}>
              <Icon d={I.lock} size={14} />
              Your free plan includes {questionnaires.filter(q => q.source_file !== null && !q.locked).length} standard templates.
              The other {lockedCount} are included in paid plans.
            </div>
          )}
        </div>
        {canEdit && (
          <button onClick={() => setCreating(true)} style={{ ...topBtn(true, isDark), padding: '12px 20px', fontSize: 15, gap: 8 }}>
            <Icon d={I.plus} size={17} /> Custom questionnaire
          </button>
        )}
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 18 }}>
        {kpis.map(k => (
          <div key={k.label} style={{ ...card(isDark), padding: '16px 18px' }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: c.slate, marginBottom: 8 }}>{k.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: k.tone || c.ink, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{loading ? '–' : k.value}</div>
            <div style={{ fontSize: 12, color: c.muted, marginTop: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={k.note}>{k.note}</div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ ...card(isDark), padding: '14px 16px', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 260px', maxWidth: 380 }}>
            <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: c.muted, display: 'flex' }}><Icon d={I.search} /></span>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search templates by name or description…"
              style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px 9px 32px', fontSize: 13.5, fontFamily: sans, color: c.ink, background: c.surface, border: `1px solid ${c.line}`, borderRadius: 8, outline: 'none' }} />
          </div>
          <div style={{ display: 'inline-flex', gap: 2, padding: 3, borderRadius: 8, background: c.surface2, border: `1px solid ${c.line}` }}>
            {([['all', 'All'], ['active', 'Active'], ['inactive', 'Inactive'], ['custom', 'Custom']] as [StatusFilter, string][]).map(([key, label]) => (
              <button key={key} style={segment(status === key)} onClick={() => setStatus(key)}>{label}<span style={badge}>{counts[key]}</span></button>
            ))}
          </div>
          <select value={sort} onChange={e => setSort(e.target.value as SortMode)} style={selectStyle} aria-label="Sort">
            <option value="recommended">Recommended order</option>
            <option value="name">Name A–Z</option>
            <option value="questions">Most questions</option>
            <option value="updated">Recently updated</option>
          </select>
          <div style={{ marginLeft: 'auto', display: 'inline-flex', gap: 2, padding: 3, borderRadius: 8, background: c.surface2, border: `1px solid ${c.line}` }}>
            {(['grid', 'list'] as const).map(v => (
              <button key={v} onClick={() => setView(v)} title={v === 'grid' ? 'Card view' : 'List view'} style={{ ...segment(view === v), padding: '7px 10px' }}>
                <Icon d={v === 'grid' ? I.grid : I.list} />
              </button>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button style={chip(category === 'all', c.accent)} onClick={() => setCategory('all')}>All frameworks</button>
          {[...CATEGORIES, OTHER].filter(cat => categoryCounts[cat.key]).map(cat => (
            <button key={cat.key} style={chip(category === cat.key, cat.color)} onClick={() => setCategory(category === cat.key ? 'all' : cat.key)}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: cat.color }} />{cat.label}
              <span style={{ fontSize: 11.5, fontWeight: 700, opacity: 0.75 }}>{categoryCounts[cat.key]}</span>
            </button>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 12.5, color: c.slate }}>
            {visible.length === questionnaires.length ? `${questionnaires.length} templates` : `${visible.length} of ${questionnaires.length}`}
            {filtersActive && <button onClick={clearFilters} style={{ marginLeft: 10, background: 'none', border: 'none', color: c.accent, fontWeight: 600, fontSize: 12.5, cursor: 'pointer', fontFamily: sans, padding: 0 }}>Clear filters</button>}
          </span>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
          {Array.from({ length: 6 }).map((_, i) => <div key={i} style={{ ...card(isDark), height: 210, animation: 'qlShimmer 1.2s infinite' }} />)}
        </div>
      ) : loadError ? (
        <div style={{ ...card(isDark), padding: '48px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 15.5, fontWeight: 600, color: c.ink, marginBottom: 6 }}>Couldn't load the library</div>
          <div style={{ fontSize: 13.5, color: c.slate, marginBottom: 14 }}>{loadError}</div>
          <button onClick={() => { libraryQuery.refetch(); }} style={{ ...topBtn(false, isDark), margin: '0 auto' }}>Try again</button>
        </div>
      ) : visible.length === 0 ? (
        <div style={{ ...card(isDark), padding: '56px 24px', textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: isDark ? '#172554' : T.blueBg, color: c.accent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
            <Icon d={I.doc} size={22} />
          </div>
          <div style={{ fontSize: 15.5, fontWeight: 600, color: c.ink, marginBottom: 6 }}>
            {questionnaires.length ? 'No templates match' : 'No questionnaires loaded yet'}
          </div>
          <div style={{ fontSize: 13.5, color: c.slate, marginBottom: 14 }}>
            {questionnaires.length ? 'Try another framework or status, or clear the search.' : 'Standard templates load automatically when the backend starts.'}
          </div>
          {questionnaires.length > 0 && <button onClick={clearFilters} style={{ ...topBtn(false, isDark), margin: '0 auto' }}>Clear filters</button>}
        </div>
      ) : view === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 14 }}>
          {visible.map(q => {
            const cat = categoryOf(q);
            const custom = q.source_file === null;
            return (
              <div key={q.id} style={{
                ...card(isDark), padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden',
                border: `1px solid ${q.is_active ? (isDark ? '#14532d' : '#BBF7D0') : c.line}`,
              }}>
                <div style={{ padding: '18px 18px 14px', flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <Tile q={q} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15.5, fontWeight: 700, color: c.ink, lineHeight: 1.3 }}>{q.name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11.5, fontWeight: 600, color: cat.color }}>{cat.label}</span>
                        {custom && <span style={{ fontSize: 11, fontWeight: 700, color: '#7C3AED', background: `#7C3AED${isDark ? '24' : '12'}`, borderRadius: 99, padding: '1px 8px' }}>Custom</span>}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: 13.5, color: q.description ? c.slate : c.muted, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                    {q.description || 'No description yet.'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12.5, color: c.muted, marginTop: 'auto' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon d={I.doc} size={13} /><strong style={{ color: c.ink, fontWeight: 700 }}>{q.item_count}</strong> question{q.item_count === 1 ? '' : 's'}</span>
                    <span>Updated {fmtDate(q.updated_at)}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '12px 18px', borderTop: `1px solid ${c.line}`, background: c.surface2 }}>
                  <Toggle q={q} />
                  <div style={{ display: 'flex', gap: 6 }}>
                    {canDelete && custom && (
                      <button onClick={() => setDeleting(q)} title="Delete this custom questionnaire" style={iconBtn(true)}><Icon d={I.trash} size={15} /></button>
                    )}
                    {canEdit && custom && (
                      <button onClick={() => navigate(`/questionnaire-library/${q.id}/builder`)} title="Add or edit this questionnaire's questions" style={{ ...topBtn(false, isDark), padding: '7px 12px', fontSize: 13 }}>
                        <Icon d={I.edit} size={13} /> Build
                      </button>
                    )}
                    {!q.locked && (
                      <button onClick={() => openPreview(q)} title="Open the full questionnaire in a new tab" style={{ ...topBtn(false, isDark), padding: '7px 12px', fontSize: 13 }}>
                        Preview <Icon d={I.external} size={13} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ ...card(isDark), padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr>
                  {['Template', 'Framework', 'Questions', 'Updated', 'Status', ''].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: c.slate, background: c.surface2, borderBottom: `1px solid ${c.line}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visible.map(q => {
                  const cat = categoryOf(q);
                  const custom = q.source_file === null;
                  const td: React.CSSProperties = { padding: '14px 16px', fontSize: 14, color: c.ink, borderBottom: `1px solid ${isDark ? '#1a1a1a' : '#EEF2F6'}`, verticalAlign: 'middle' };
                  return (
                    <tr key={q.id}>
                      <td style={td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <Tile q={q} size={36} />
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                              {q.name}
                              {custom && <span style={{ fontSize: 11, fontWeight: 700, color: '#7C3AED', background: `#7C3AED${isDark ? '24' : '12'}`, borderRadius: 99, padding: '1px 8px' }}>Custom</span>}
                            </div>
                            {q.description && <div style={{ fontSize: 12.5, color: c.muted, marginTop: 2, maxWidth: 520, whiteSpace: 'pre-wrap' }}>{q.description}</div>}
                          </div>
                        </div>
                      </td>
                      <td style={td}><span style={{ fontSize: 12.5, fontWeight: 600, color: cat.color, whiteSpace: 'nowrap' }}>{cat.label}</span></td>
                      <td style={{ ...td, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{q.item_count}</td>
                      <td style={{ ...td, color: c.slate, whiteSpace: 'nowrap' }}>{fmtDate(q.updated_at)}</td>
                      <td style={td}><Toggle q={q} /></td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 6 }}>
                          {canDelete && custom && <button onClick={() => setDeleting(q)} title="Delete this custom questionnaire" style={iconBtn(true)}><Icon d={I.trash} size={15} /></button>}
                          {canEdit && custom && (
                            <button onClick={() => navigate(`/questionnaire-library/${q.id}/builder`)} title="Add or edit this questionnaire's questions" style={{ ...topBtn(false, isDark), padding: '7px 12px', fontSize: 13 }}>
                              <Icon d={I.edit} size={13} /> Build
                            </button>
                          )}
                          {!q.locked && <button onClick={() => openPreview(q)} style={{ ...topBtn(false, isDark), padding: '7px 12px', fontSize: 13 }}>Preview <Icon d={I.external} size={13} /></button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1500, display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 18px', borderRadius: 10, fontSize: 14, fontFamily: sans, animation: 'qlToast .18s ease-out',
          background: toast.error ? '#DC2626' : (isDark ? '#e5e7eb' : '#0F172A'), color: toast.error ? '#fff' : (isDark ? '#0F172A' : '#fff'),
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
        }}>
          {!toast.error && <Icon d={I.check} />}{toast.text}
        </div>
      )}

      {creating && (
        <CreateQuestionnaireModal
          isDark={isDark}
          onClose={() => setCreating(false)}
          onCreated={q => { patchList(list => [q, ...list.filter(x => x.id !== q.id)]); setCreating(false); navigate(`/questionnaire-library/${q.id}/builder`); }}
        />
      )}

      {deleting && (
        <DeleteModal
          q={deleting} isDark={isDark}
          onClose={() => setDeleting(null)}
          onDeleted={() => { patchList(list => list.filter(x => x.id !== deleting.id)); setToast({ text: `"${deleting.name}" deleted.` }); setDeleting(null); }}
        />
      )}
    </div>
  );
}

function ModalShell({ isDark, busy, onClose, children, width = 520 }: { isDark: boolean; busy: boolean; onClose: () => void; children: React.ReactNode; width?: number }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);
  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, padding: 24 }}>
      <div style={{ width: '100%', maxWidth: width, background: isDark ? '#0d0d0d' : T.surface, border: `1px solid ${isDark ? '#1f1f1f' : T.bdr}`, borderRadius: 14, overflow: 'hidden', fontFamily: sans, animation: 'qlPop .16s ease-out' }}>
        {children}
      </div>
    </div>
  );
}

function CreateQuestionnaireModal({ isDark, onClose, onCreated }: {
  isDark: boolean; onClose: () => void; onCreated: (q: Questionnaire) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const createMutation = useMutation({
    mutationFn: async (body: { name: string; description: string | null }) =>
      (await api.post('/v1/questionnaire-library/', body)).data as Questionnaire,
    onSuccess: data => onCreated(data),
    onError: err => setError(apiError(err, 'Could not create the questionnaire. Try again.')),
    meta: { invalidates: [['questionnaire-library']] },
  });
  const saving = createMutation.isPending;

  const ink = isDark ? '#e5e7eb' : T.ink, slate = isDark ? '#94a3b8' : T.slate, muted = isDark ? '#64748b' : T.muted;
  const line = isDark ? '#1f1f1f' : T.bdr, surface = isDark ? '#0d0d0d' : T.surface, surface2 = isDark ? '#141414' : T.surface2;

  const input: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14, fontFamily: sans, color: ink,
    background: surface, border: `1px solid ${line}`, borderRadius: 8, outline: 'none',
  };

  const save = () => {
    if (!name.trim()) { setError('Enter a name.'); return; }
    setError('');
    createMutation.mutate({ name: name.trim(), description: description.trim() || null });
  };

  return (
    <ModalShell isDark={isDark} busy={saving} onClose={onClose} width={560}>
      <form noValidate onSubmit={e => { e.preventDefault(); if (!saving) save(); }}>
        <div style={{ padding: '22px 24px 0', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 19, fontWeight: 700, color: ink }}>New custom questionnaire</div>
            <div style={{ fontSize: 13.5, color: slate, marginTop: 4, lineHeight: 1.5 }}>
              Creates an empty template in the library. You'll add questions next, in the builder.
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: surface2, border: `1px solid ${line}`, color: slate, cursor: 'pointer', width: 32, height: 32, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon d={I.close} />
          </button>
        </div>
        <div style={{ padding: '20px 24px', display: 'grid', gap: 16 }}>
          <div>
            <label htmlFor="new-q-name" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: ink, marginBottom: 6 }}>Name</label>
            <input id="new-q-name" style={input} value={name} autoFocus maxLength={200} onChange={e => setName(e.target.value)} placeholder="e.g. Annual vendor security review" />
          </div>
          <div>
            <label htmlFor="new-q-desc" style={{ display: 'block', fontSize: 13, fontWeight: 600, color: ink, marginBottom: 6 }}>
              Description <span style={{ color: muted, fontWeight: 500 }}>(optional)</span>
            </label>
            <textarea id="new-q-desc" style={{ ...input, minHeight: 110, resize: 'vertical', lineHeight: 1.55 }} value={description} maxLength={2000} onChange={e => setDescription(e.target.value)}
              placeholder="What this questionnaire covers and when to send it." />
            <div style={{ fontSize: 12, color: muted, textAlign: 'right', marginTop: 4 }}>{description.length}/2000</div>
          </div>
          {error && <div style={{ fontSize: 13.5, color: '#DC2626' }} role="alert">{error}</div>}
        </div>
        <div style={{ padding: '14px 24px', borderTop: `1px solid ${line}`, background: surface2, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} disabled={saving} style={topBtn(false, isDark)}>Cancel</button>
          <button type="submit" disabled={saving || !name.trim()} style={{ ...topBtn(true, isDark), opacity: saving || !name.trim() ? 0.55 : 1 }}>
            {saving ? 'Creating…' : 'Create questionnaire'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function DeleteModal({ q, isDark, onClose, onDeleted }: { q: Questionnaire; isDark: boolean; onClose: () => void; onDeleted: () => void }) {
  const [error, setError] = useState('');
  const ink = isDark ? '#e5e7eb' : T.ink, slate = isDark ? '#94a3b8' : T.slate;
  const line = isDark ? '#1f1f1f' : T.bdr, surface2 = isDark ? '#141414' : T.surface2;

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/v1/questionnaire-library/${q.id}`),
    onSuccess: () => onDeleted(),
    onError: err => setError(apiError(err, 'Could not delete the questionnaire. Try again.')),
    meta: { invalidates: [['questionnaire-library']] },
  });
  // Stays busy after success until the parent closes the modal, as before.
  const busy = deleteMutation.isPending || deleteMutation.isSuccess;
  const run = () => {
    setError('');
    deleteMutation.mutate();
  };

  return (
    <ModalShell isDark={isDark} busy={busy} onClose={onClose}>
      <div style={{ padding: '22px 24px', display: 'flex', gap: 14 }}>
        <span style={{ width: 42, height: 42, borderRadius: 12, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#DC2626', background: isDark ? '#2a0f0f' : T.redBg }}>
          <Icon d={I.trash} size={20} />
        </span>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: ink }}>Delete "{q.name}"?</div>
          <div style={{ fontSize: 14, color: slate, marginTop: 6, lineHeight: 1.55 }}>
            This custom template and its {q.item_count} question{q.item_count === 1 ? '' : 's'} are removed for good. This can't be undone.
          </div>
          {error && <div style={{ fontSize: 13.5, color: '#DC2626', marginTop: 10 }}>{error}</div>}
        </div>
      </div>
      <div style={{ padding: '14px 24px', borderTop: `1px solid ${line}`, background: surface2, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button onClick={onClose} disabled={busy} style={topBtn(false, isDark)}>Cancel</button>
        <button onClick={run} disabled={busy} style={{ ...topBtn(true, isDark), background: '#DC2626', borderColor: '#DC2626', color: '#fff', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </ModalShell>
  );
}
